// Self-healing Zustand store registry.
//
// Every Zustand store in this app is created via `createTrackedStore` (a
// drop-in wrapper around Zustand's `create`) which auto-registers the
// resulting store in `STORE_REGISTRY`. The DevToolkit's "Zustand" tab
// reads from this registry to render every store's live state without
// any per-store wiring.
//
// Three guardrails keep the registry exhaustive:
//   1. The wrapper itself — engineers literally use a different import
//      than bare `create`, so registration happens at the source.
//   2. A precommit lint (`scripts/check-zustand-store-wrapper.sh`)
//      blocks any new code that imports `create` from `'zustand'`
//      outside this one file. So a contributor cannot accidentally
//      bypass the wrapper.
//   3. A precommit code generator
//      (`scripts/generate-zustand-store-manifest.ts`) globs every
//      `createTrackedStore<` callsite and emits a side-effect import
//      manifest at `./zustand-store-manifest.generated.ts`. The
//      DevToolkit Zustand tab imports the manifest as its first line,
//      forcing every store's module to evaluate (which triggers
//      registration) before the tab queries the registry. Without
//      this, lazily-loaded stores (e.g. ones only imported by a
//      route the user hasn't visited yet) would be invisible.
//
// Runtime cost in production:
//   - ~1KB of code in the bundle (the wrapper + a Map)
//   - One Map.set per store at app boot (~29 entries × <1µs)
//   - Zero per-state-update overhead — the wrapper hands back the
//     same store instance Zustand would have, untouched.
// The DevToolkit tab UI itself is code-split into the DevToolkit chunk
// which only loads for `NODE_ENV=development` OR `@morfi.markets`
// users — so prod public-user bundles never include the inspector.

import { create, type StoreApi, type UseBoundStore } from 'zustand';

interface StoreEntry {
  /** Stable identifier — typically the export name (e.g. `useDevToolkitStore`). */
  name: string;
  /** Live store reference. Components subscribe normally via `entry.store(selector)`. */
  // ALLOW-UNKNOWN: registry holds heterogeneous stores from across the app; the concrete state shape is per-store and not knowable here.
  store: UseBoundStore<StoreApi<unknown>>;
  /**
   * Persisted localStorage key, when the store was created with
   * Zustand's `persist` middleware. `null` for in-memory-only stores.
   * Detected via the `persist` API the middleware attaches to the
   * store function — `store.persist.getOptions().name`.
   */
  persistKey: string | null;
}

const STORE_REGISTRY: Map<string, StoreEntry> = new Map<string, StoreEntry>();

/**
 * Drop-in replacement for Zustand's `create<T>()`. Captures the resulting
 * store in `STORE_REGISTRY` keyed by `name` so the DevToolkit Zustand
 * inspector can enumerate every store without per-store wiring.
 *
 * Usage mirrors `create` exactly:
 *
 *   // Plain store
 *   export const useFooStore = createTrackedStore<FooState>('useFooStore')(
 *     (set) => ({ count: 0, increment: () => set((s) => ({ count: s.count + 1 })) })
 *   );
 *
 *   // With persist middleware (or any other Zustand middleware)
 *   export const useBarStore = createTrackedStore<BarState>('useBarStore')(
 *     persist(
 *       (set) => ({ ... }),
 *       { name: 'bar-store' }
 *     )
 *   );
 *
 * The `name` argument should match the export name — this is what the
 * DevToolkit displays. The generator script enforces uniqueness across
 * the codebase.
 */
export function createTrackedStore<T>(name: string): ReturnType<typeof create<T>> {
  const baseCurry = create<T>();
  return ((initializer: Parameters<typeof baseCurry>[0]): ReturnType<typeof baseCurry> => {
    const store = baseCurry(initializer);
    // ALLOW-UNKNOWN: type-erasing the store at the registry boundary is intentional — see StoreEntry.store.
    register(name, store as UseBoundStore<StoreApi<unknown>>);
    return store;
  }) as ReturnType<typeof create<T>>;
}

/**
 * Detect the localStorage key used by the `persist` middleware, if any.
 * The middleware attaches a `persist` API object to the store function
 * with `getOptions()` returning the original config including `name`.
 * Stores created without `persist` won't have this property — return
 * `null` so the UI can render an "in-memory only" badge.
 */
// ALLOW-UNKNOWN: helper accepts the type-erased store from the registry — see StoreEntry.store.
function detectPersistKey(store: UseBoundStore<StoreApi<unknown>>): string | null {
  // ALLOW-UNKNOWN: persist middleware attaches optional API at runtime; check defensively.
  const persistApi = (store as unknown as { persist?: { getOptions?: () => { name?: string } } })
    .persist;
  if (persistApi === undefined || typeof persistApi.getOptions !== 'function') {
    return null;
  }
  try {
    const opts = persistApi.getOptions();
    return typeof opts?.name === 'string' && opts.name.length > 0 ? opts.name : null;
  } catch {
    // SILENT-CATCH: defensive — third-party middleware shape variations shouldn't break the registry.
    return null;
  }
}

// ALLOW-UNKNOWN: registry entry boundary — heterogeneous store shapes type-erased here.
function register(name: string, store: UseBoundStore<StoreApi<unknown>>): void {
  STORE_REGISTRY.set(name, {
    name,
    store,
    persistKey: detectPersistKey(store),
  });
}

// Dev-only window exposure for e2e harnesses (Playwright). Lets specs drive
// any tracked Zustand store directly when in-app reset logic (e.g. dialog
// "reset to step 1 on open" useEffects) makes localStorage-based seeding
// fragile. Gated to non-prod and SSR-safe.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  // ALLOW-UNKNOWN: augmenting `window` with the dev-only registry handle for e2e harness access.
  const w = window as unknown as { __MORFI_ZUSTAND_REGISTRY__?: Map<string, StoreEntry> };
  w.__MORFI_ZUSTAND_REGISTRY__ = STORE_REGISTRY;
}

/**
 * Snapshot of every registered store, sorted by name. Consumed by the
 * DevToolkit Zustand inspector. Re-call on demand — a new store added
 * via `createTrackedStore` after this returns won't appear in the
 * cached array.
 */
export function getRegisteredStores(): readonly StoreEntry[] {
  return Array.from(STORE_REGISTRY.values()).sort((a, b) => a.name.localeCompare(b.name));
}
